import { useState } from "react";
import { userRoles } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Package } from "@phosphor-icons/react";
import { toast } from "sonner";
import { apiError } from "@/api/client";

export default function Login() {
  const { user, login, ready } = useAuth();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const loc = useLocation();

  if (ready && user) {
    const dest = userRoles(user).includes("superuser") ? "/admin-console" : (loc.state?.from?.pathname || "/");
    return <Navigate to={dest} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(mobile.trim(), password, rememberMe);
      toast.success("Welcome back");
      if (userRoles(u).includes("superuser")) {
        // Force redirect after state settles
        window.location.href = "/admin-console";
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(255,237,213,0.9), rgba(255,183,77,0.75)), url('https://images.pexels.com/photos/257636/pexels-photo-257636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-orange-950">
          <div className="label-cap text-orange-800">Warehouse Control Room</div>
          <h1 className="font-display text-5xl font-black mt-2 leading-none">
            Order to Dispatch<br />in real-time.
          </h1>
          <p className="mt-4 text-orange-900/80 max-w-md">
            One command room for orders, picking, scanning, packing, and dispatch — with role-based
            entries and live color-coded status boards.
          </p>
          <div className="mt-8 grid grid-cols-4 gap-2 max-w-md">
            {["bg-emerald-500","bg-amber-500","bg-orange-500","bg-blue-500","bg-violet-500","bg-stone-500","bg-rose-500","bg-orange-800"].map((c,i)=>(
              <div key={i} className={`h-2 rounded-sm ${c}`} />
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16 bg-orange-50">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-sm bg-orange-600 text-white grid place-items-center">
              <Package size={20} weight="fill" />
            </div>
            <div>
              <div className="label-cap">Order & Dispatch</div>
              <div className="font-display font-black text-lg leading-none">Sign in</div>
            </div>
          </div>

          <label className="label-cap block mb-1.5">Mobile Number</label>
          <Input
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder="10-digit mobile number"
            data-testid="login-mobile-input"
            className="mb-4"
            required
          />

          <label className="label-cap block mb-1.5">Password</label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="login-password-input"
            className="mb-4"
            required
          />

          <label className="flex items-center gap-2 mb-6 cursor-pointer select-none text-sm text-slate-700" data-testid="login-remember-label">
            <Checkbox
              checked={rememberMe}
              onCheckedChange={(v) => setRememberMe(!!v)}
              data-testid="login-remember-checkbox"
            />
            Stay logged in until I log out
          </label>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-sm h-11"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
